import React, { useId, useRef, useState, useEffect, forwardRef } from 'react';
import Icon from '../icon/Icon';

export const CollapsibleHeader = forwardRef(function CollapsibleHeader(
  {
    IconComponent,
    title,
    subtitle,
    leading,
    extra,
    actions,
    children,
    defaultOpen = false,
    onToggle,
    disabled = false,
    className = '',
    as = 'header',
    expandedHeight = 'auto', // "auto" | number | string (es. "100vh", "32rem")
    collapsedHeight = 0, // number | string
    style,
    ...rest
  },
  ref
) {
  const panelId = useId();
  const [open, setOpen] = useState(Boolean(defaultOpen));
  const contentRef = useRef(null);

  const prefersReduced =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Helper per normalizzare il valore height
  const toCssHeight = (value) => {
    if (value === 'auto') return 'auto';
    if (typeof value === 'number') return `${value}px`;
    return value; // string già con unità (es. "100vh", "32rem")
  };

  const setHeight = (value) => {
    const el = contentRef.current;
    if (!el) return;
    el.style.height = toCssHeight(value);
  };

  const getExpandedTarget = () => {
    // Se l’altezza è "auto", per l’animazione usiamo lo scrollHeight
    if (expandedHeight === 'auto') {
      const el = contentRef.current;
      if (!el) return 'auto';
      return el.scrollHeight; // numero -> px
    }
    return expandedHeight; // valore custom (px | vh | rem | ecc.)
  };

  const toggle = () => {
    if (disabled) return;
    const next = !open;
    const el = contentRef.current;

    if (prefersReduced || !el) {
      setOpen(next);
      onToggle && onToggle(next);
      requestAnimationFrame(() => setHeight(next ? expandedHeight : collapsedHeight));
      return;
    }

    if (next) {
      // Apri
      setHeight(collapsedHeight);
      requestAnimationFrame(() => {
        setOpen(true);
        onToggle && onToggle(true);
        const target = getExpandedTarget();
        setHeight(target);
      });
    } else {
      // Chiudi
      const current = getComputedStyle(el).height;
      if (current === 'auto') {
        setHeight(el.scrollHeight); // forza punto di partenza numerico
        // forza reflow
        // eslint-disable-next-line no-unused-expressions
        el.offsetHeight;
      }
      setOpen(false);
      onToggle && onToggle(false);
      setHeight(collapsedHeight);
    }
  };

  // A fine transizione, se aperto e l’altezza è "auto" -> height:auto
  useEffect(() => {
    const el = contentRef.current;
    if (!el || prefersReduced) return;
    const onEnd = (e) => {
      if (e.propertyName !== 'height') return;
      if (open && expandedHeight === 'auto') {
        setHeight('auto');
      }
    };
    el.addEventListener('transitionend', onEnd);
    return () => el.removeEventListener('transitionend', onEnd);
  }, [open, prefersReduced, expandedHeight]);

  // Sincronizza altezza quando cambia il contenuto o lo stato
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    if (prefersReduced) {
      setHeight(open ? expandedHeight : collapsedHeight);
      return;
    }

    if (open) {
      if (expandedHeight === 'auto') {
        // se in transizione, porta al valore numerico; al termine metteremo "auto"
        const current = getComputedStyle(el).height;
        if (current !== 'auto') {
          setHeight(el.scrollHeight);
        } else {
          setHeight('auto');
        }
      } else {
        setHeight(expandedHeight);
      }
    } else {
      setHeight(collapsedHeight);
    }
  }, [children, open, prefersReduced, expandedHeight, collapsedHeight]);

  const HeaderTag = as;

  // Altezza iniziale (SSR/first render) per evitare flash
  const initialHeight = open
    ? toCssHeight(expandedHeight === 'auto' ? 'auto' : expandedHeight)
    : toCssHeight(collapsedHeight);

  return (
    <div className={className + ' sticky-top'} style={style} {...rest}>
      <HeaderTag
        ref={ref}
        className="pages-header"
        aria-controls={panelId}
        aria-expanded={open}
        style={style}
      >
        <div className="pages-header__left">
          <div className="pages-header__titles d-flex align-items-center">
            {leading && <div className="pages-header__leading mr-3">{leading}</div>}
            {IconComponent && (
              <div className="pages-header__icon mr-3">
                <IconComponent />
              </div>
            )}
            <div>
              {title && <h5 className="pages-header__title">{title}</h5>}
              {subtitle && <small className="pages-header__subtitle">{subtitle}</small>}
            </div>
          </div>
        </div>

        <div className="pages-header__extra">{extra}</div>

        <div className="pages-header__actions">
          {actions}
          <button
            type="button"
            onClick={toggle}
            disabled={disabled}
            aria-controls={panelId}
            aria-expanded={open}
            className="btn"
          >
            <Icon
              name="chevronDown"
              aria-hidden="true"
              aria-expanded={open}
              style={{
                fontSize: '1rem',
                color: '#6c757d',
                display: 'inline-block',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: `transform 200ms ease`,
              }}
            />
          </button>
        </div>
      </HeaderTag>

      <section
        id={panelId}
        ref={contentRef}
        style={{
          height: initialHeight,
          overflowX: 'hidden',
          overflowY: 'visible',
          transition: prefersReduced ? 'none' : 'height 200ms ease-out',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          marginLeft: '4.6rem',
          paddingLeft: '1rem',
          paddingRight: '1rem',
          borderRadius: '0.5rem',
          backgroundColor: '#fff',
        }}
        aria-hidden={!open}
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={0}
      >
        {children}
      </section>
    </div>
  );
});
